import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useMediaQuery } from '@mui/material'
import { theme } from 'mui-config'

import { updateProfile as updateProfileApi } from 'api/user'

import { useAuth } from './AuthProvider'
import { getOnboardingFromStorage } from 'utils/helpers'
import { getCampaigns } from 'api/campaigns'

import { useAnalytics } from './AnalyticsProvider'
import { ANALYTICS_EVENT_NAMES } from 'constants/analytics'

export interface IOnboardingMenuStep {
  id: number
  text: string
  checked: boolean
  buttonText: string
  onClick: () => void
  hidden?: boolean
}

interface OnboardingContextProps {
  steps: IOnboardingMenuStep[]
  isOpen: boolean
  onClose: () => void
  onOpen: () => void
  uncheckedSteps: number | undefined
  onStepClick: () => void
}

const OnboardingContext = createContext<OnboardingContextProps>({
  steps: [],
  isOpen: false,
  onClose: () => null,
  onOpen: () => null,
  uncheckedSteps: 0,
  onStepClick: () => null,
})

const OnboardingProvider = ({
  children,
  onStepClick = () => null,
}: {
  children: React.ReactNode
  onStepClick?: () => void
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [changed, setChanged] = useState(false)
  const { user, isProfileFulfilled } = useAuth()
  const client = useQueryClient()
  const navigate = useNavigate()
  const { analyticsTrack } = useAnalytics()
  const updateProfile = useMutation({
    mutationFn: updateProfileApi,
    onSuccess: () => {
      client.invalidateQueries(['user'])
    },
  })
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const { data: campaigns = [] } = useQuery({
    queryKey: ['campaigns'],
    queryFn: getCampaigns,
  })

  useEffect(() => {
    const onboarding = getOnboardingFromStorage()

    if (
      user?.profile?.isOnboardingCompleted ||
      onboarding?.skip ||
      !isProfileFulfilled
    )
      return
    setIsOpen(true)
  }, [user?.profile?.isOnboardingCompleted, isProfileFulfilled])

  useEffect(() => {
    if (isOpen) {
      analyticsTrack(`${ANALYTICS_EVENT_NAMES.onboarding}: Launched`)
    }
  }, [isOpen])

  const steps = useMemo(() => {
    const onboarding = getOnboardingFromStorage()
    const appliedCampaigns = campaigns?.find(
      (campaign) => campaign.creators.length
    )

    const isUserFiltered = !!user?.isFilterGroup

    return [
      {
        id: 1,
        text: 'Complete your profile',
        checked: isProfileFulfilled,
        buttonText: 'Complete',
        onClick: () => {
          return null
        },
      },
      {
        id: 2,
        text: 'Review Hue Community Member Resources',
        checked: isUserFiltered ? true : !!onboarding?.resources,
        buttonText: 'Go to Resources',
        onClick: () => {
          localStorage.setItem(
            'onboarding',
            JSON.stringify({
              ...onboarding,
              resources: true,
            })
          )
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.onboarding}: Viewed Resources`
          )
          setChanged((prev) => !prev)
          navigate('/resources')
        },
        hidden: isUserFiltered,
      },
      {
        id: 3,
        text: 'Review and verify profile information',
        checked: !!onboarding?.profile,
        buttonText: 'Go to Profile',
        onClick: () => {
          localStorage.setItem(
            'onboarding',
            JSON.stringify({
              ...onboarding,
              profile: true,
            })
          )
          analyticsTrack(`${ANALYTICS_EVENT_NAMES.onboarding}: Viewed Profile`)
          setChanged((prev) => !prev)
          navigate('/profile')
        },
      },
      {
        id: 4,
        text: 'Apply to your first campaign',
        checked: !!appliedCampaigns,
        buttonText: 'Go to Campaigns',
        onClick: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.onboarding}: Viewed Campaigns`
          )
          navigate('/campaigns?tab=1')
        },
      },
      {
        id: 5,
        text: 'Add Discord username to profile',
        checked: isUserFiltered ? true : !!user?.profile?.discordHandle,
        buttonText: 'Add username',
        onClick: () => {
          analyticsTrack(
            `${ANALYTICS_EVENT_NAMES.onboarding}: Viewed Discord Username Field`
          )
          navigate('/profile?block=personalInformation')
        },
        hidden: isUserFiltered,
      },
    ]
  }, [user, campaigns, changed])

  useEffect(() => {
    const isAllChecked = !steps.find((el) => !el.checked)
    if (isAllChecked && !user?.profile?.isOnboardingCompleted) {
      updateProfile.mutate({ isOnboardingCompleted: true })
    }
  }, [steps, user])

  const onOpen = () => {
    const onboarding = getOnboardingFromStorage()

    localStorage.setItem(
      'onboarding',
      JSON.stringify({
        ...onboarding,
        skip: false,
      })
    )
    setIsOpen(true)
  }

  const onClose = () => {
    const onboarding = getOnboardingFromStorage()
    const uncheckedStep = steps.find((el) => !el.checked)

    localStorage.setItem(
      'onboarding',
      JSON.stringify({
        ...onboarding,
        skip: true,
      })
    )
    analyticsTrack(`${ANALYTICS_EVENT_NAMES.onboarding}: Closed`, {
      step: uncheckedStep?.text || '',
    })
    setIsOpen(false)
  }

  const _onStepClick = () => {
    if (isMobile) {
      onClose()
    }
    onStepClick()
  }

  const uncheckedSteps = steps.filter((el) => !el.checked).length

  return (
    <OnboardingContext.Provider
      value={{
        onStepClick: _onStepClick,
        steps,
        isOpen,
        onClose,
        onOpen,
        uncheckedSteps,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}

export const useOnboarding = () => useContext(OnboardingContext)

export default OnboardingProvider
